import { ReactNode, useEffect, useRef, useState } from 'react';

import { FormProvider, useForm } from 'react-hook-form';

import { EMPTY_FORM, TMobileSignupForm } from './form';
import { applicationToForm, stateToForm } from './mapping';
import { MobileSignupStatusProvider } from './MobileSignupStatusContext';
import {
  useGetApplicationQuery,
  useGetSelectStateQuery,
} from '../../../api/lendingApi/lendingApiQueries';
import { TGetStateResponseDE } from '../../../lapiOverrideTypesDE';

export const MobileSignupFormProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { application, applicationStatus } = useGetApplicationQuery();
  const { normalisedState } = useGetSelectStateQuery((data) =>
    stateToForm((data as TGetStateResponseDE).data),
  );

  const methods = useForm<TMobileSignupForm>({
    // Keep fields registered across screen unmounts so values persist for the whole flow.
    shouldUnregister: false,
    defaultValues: EMPTY_FORM,
  });

  const [isSeeded, setIsSeeded] = useState(false);
  const hasSeeded = useRef(false);

  // defaultValues are captured at mount, so the async GETs need an explicit reset once
  // they resolve. State and application refetch independently after the account mint; seed
  // only once BOTH have resolved, otherwise a state-first reset applies amount: undefined
  // and wipes the non-dirty amount default, collapsing the nav ceiling back to step 1.
  useEffect(() => {
    if (hasSeeded.current) return;
    if (!normalisedState || applicationStatus !== 'success') return;
    hasSeeded.current = true;
    methods.reset(
      { ...EMPTY_FORM, ...normalisedState, ...applicationToForm(application) },
      { keepDirtyValues: true },
    );
    setIsSeeded(true);
  }, [normalisedState, application, applicationStatus, methods]);

  return (
    <MobileSignupStatusProvider value={{ isSeeded }}>
      <FormProvider {...methods}>{children}</FormProvider>
    </MobileSignupStatusProvider>
  );
};
